import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router"
import { toast } from "react-toastify"
import Server from "../../Shared/Server"
import Spinner from "../../Shared/Spinner"
import {InputField} from "../../Shared/InputField"
import SelectField from "../../Shared/SelectField"
import { hours_list } from "../../Shared/List"
import AsyncSelectField from "../../Shared/AsyncSelect"

const MotorReportForm = (props) => {
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [motorOptions, setMotorOptions] = useState([{id: "", name: '-- Please choose motor --', key: null}])
    const [hourOptions, setHourOptions] = useState([{id: "", name: '-- Please choose hour --', key: null}])

    const [formData, setFormData] = useState({
        hour: "",
        motor_id: "",
        previous_reading: 0,
        current_reading: 0,
        difference: 0,
        fuel: 0,
        date: new Date().toISOString().slice(0, 10),
    })
    const {hour, motor_id, previous_reading, current_reading, difference, fuel, date} = formData

    const navigate = useNavigate()

    // Load options dynamically from a remote source
    const loadOptions = async (url, query, setOptions) => {
        const params = { q: query };

        setHourOptions(hours_list.map(hour => ({
            id: hour.id, 
            key: hour.key, 
            name: hour.name
        })));

        try {
            const response = await Server.get(`/options/${url}`, {params});
            setOptions(response.data);
            return response.data;
        } catch (error) {
            return [];
        }
    };

    const debounceRef = useRef(null)

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            const curr = parseFloat(current_reading) || 0
            const prev = parseFloat(previous_reading) || 0
            if (curr > 0 && curr < prev) {
                setFormData((prevState) => ({
                    ...prevState,
                    current_reading: 0,
                    difference: 0,
                }))
                toast.warning('Current reading cannot be less than previous reading')
            } else {
                setFormData((prevState) => ({
                    ...prevState,
                    difference: curr - prev,
                }))
            }
        }, 1500)
        return () => clearTimeout(debounceRef.current)
    }, [current_reading, previous_reading])

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

        if (e.target.name === "motor_id"){
            Server.get(`/options/motor-reading-detail/${e.target.value}`).then(function(response){
                if (response.status === 200 & response.data.max_reading !== "None"){
                    setFormData((prev) => ({
                        ...prev,
                        previous_reading: parseInt(response.data.max_reading)
                    }))
                }
            })
        }
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const payload = {motor_id, previous_reading, current_reading, difference, fuel, date, hour}
        addReport(payload)
    }
    
    // add employee
    const addReport = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.post('/motor-reports/', payload)
            if (response.status === 201){
                toast.success('Successfully created')
                navigate('/motor-daily-reports')
                setIsLoading(false)
            }else{
                toast.error(response.data?.detail)
            }
        } catch (error) {
            setIsLoading(false)
            if(error.response.status === 401){
                navigate('/login')
            }else if (error.response.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error.response.data.detail)
            }
        }
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="card">
            <div className="card-header bg-info p-2 justify-content-center">
                <h3 className="card-title">Add Motor Report Form</h3>
            </div>
            <div className="card-body">
                <form onSubmit={onSubmit}>
                    <div className="row">
                        <div className="col-md-3">
                            <InputField
                                name="date"
                                type="date"
                                label="Date"
                                value={date}
                                onChange={onChange}
                                error={errors.date}
                            />
                        </div>
                        <div className="col-md-3">
                            <SelectField
                                name="hour"
                                label="Hour"
                                value={parseInt(hour)}
                                options={hourOptions}
                                onChange={onChange}
                                error={errors.hour}
                            />
                        </div>
                        <div className="col-md-3">
                            <AsyncSelectField
                                name="motor_id"
                                label="Motor"
                                value={motor_id}
                                onChange={onChange}
                                loadOptions={(query) => loadOptions('motors', query, setMotorOptions)}
                                options={motorOptions}
                                placeholder="Start typing to search..."
                                error={errors.motor_id}
                            />
                        </div>
                        <div className="col-md-3">
                            <InputField
                                placeholder="Previous Reading"
                                name="previous_reading"
                                type="number"
                                disabled="disabled"
                                label="Previous Reading"
                                value={parseInt(previous_reading)}
                                onChange={onChange}
                                error={errors.previous_reading}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <InputField
                                placeholder="Current Reading"
                                name="current_reading"
                                type="number"
                                label="Current Reading"
                                value={parseInt(current_reading)}
                                onChange={onChange}
                                error={errors.current_reading}
                            />
                        </div>
                        <div className="col-md-4">
                            <InputField
                                placeholder="Difference"
                                name="difference"
                                type="number"
                                disabled="disabled"
                                label="Difference"
                                value={parseInt(difference)}
                                onChange={onChange}
                                error={errors.difference}
                            />
                        </div>
                        <div className="col-md-4">
                            <InputField
                                placeholder="Fuel"
                                name="fuel"
                                type="number"
                                label="Fuel"
                                value={parseInt(fuel)}
                                onChange={onChange}
                                error={errors.fuel}
                            />
                        </div>
                    </div>
                    <br/>
                    <div className="row">
                        <div className="col-md-12">
                            <input type="submit" className="btn btn-outline-info" value="Save Report" style={{float: 'right'}}/>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <br/>
    </>
  )
}

export default MotorReportForm
